import { map } from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {TypedPaginationResponse} from '../../../responses/pagination.response';
import {GlossaryTerm} from '../models/glossary-term.model';
import {RequestQueryBuilder} from '@nestjsx/crud-request';
import {Burly} from 'kb-burly';

@Injectable({
  providedIn: 'root'
})
export class TorchGlossaryService {
  private types = [
    'Abbreviation',
    'Acronym',
    'Definition'
  ];

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }

  listTypes(): string[] {
    return this.types;
  }

  fetchTerms(searchText: string | null,
             startsWith: string | null,
             type: string | null,
             sortBy: string | null = 'Term',
             sortDirection: 'ASC' | 'DESC' | '' = 'ASC',
             page: number,
             limit: number = 15): Observable<TypedPaginationResponse<GlossaryTerm>> {
    let offset = page * limit;
    let queryString = RequestQueryBuilder.create({
      limit,
      offset
    });

    if (!!type && type.length > 0) {
      if (!!searchText && searchText.length > 0) {
        if (!!startsWith && startsWith) {
          queryString = queryString.search({
            '$and': [
              // {term: {'$cont': searchText}},
              {'$or': [
                {term: {'$cont': searchText}},
                {definition: {'$cont': searchText}},
                {extendedDefinition: {'$cont': searchText}},
              ]},
              {type: {'$cont': type}},
              {term: {'$startsL': startsWith}}
            ]
          });
        } else {
          queryString = queryString.search({
            '$and': [
              {'$or': [
                {term: {'$cont': searchText}},
                {definition: {'$cont': searchText}},
                {extendedDefinition: {'$cont': searchText}},
              ]},
              {type: {'$cont': type}}
            ]
          });
        }
      } else {
        if (!!startsWith && startsWith) {
          queryString = queryString.search({
            '$and': [
              {type: {'$cont': type}},
              {term: {'$startsL': startsWith}}
            ]
          });
        } else {
          queryString = queryString.search({
            type: {'$cont': type}
          });
        }
      }

    } else if (!!searchText && searchText.length > 0) {
      if (!!startsWith && startsWith) {
        queryString = queryString.search({
          '$and': [
            {'$or': [
              {term: {'$cont': searchText}},
              {definition: {'$cont': searchText}},
              {extendedDefinition: {'$cont': searchText}},
            ]},
            {term: {'$startsL': startsWith}}
          ]
        });
      } else {
        queryString = queryString.search({
          '$or': [
            {term: {'$cont': searchText}},
            {definition: {'$cont': searchText}},
            {extendedDefinition: {'$cont': searchText}},
          ],
        });
      }
    } else if (!!startsWith && startsWith) {
      queryString = queryString.search({
        term: {'$startsL': startsWith}
      });
    }

    queryString = queryString.sortBy({
      field: sortBy || 'Term',
      order: sortDirection || 'ASC'
    });


    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/documentation/glossary')
      .appendRawQueryString(queryString.query(false))
      .get;

    return this.httpClient.get<TypedPaginationResponse<GlossaryTerm>>(endpointURL);
  }

  createTerm(data: {term: string, definition: string, extendedDefinition: string, createdBy: string, type: string}) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/documentation/glossary')
      .get;

    return this.httpClient.post(endpointURL, data);
  }

  deleteTerm(id: number) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/documentation/glossary')
      .addSegment(`/${id}`)
      .get;

    return this.httpClient.delete(endpointURL);
  }

  updateTerm(id: number, partialTerm: Partial<GlossaryTerm>) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/documentation/glossary')
      .addSegment(`/${id}`)
      .get;

    return this.httpClient.patch(endpointURL, partialTerm);
  }

  getTerm(id: number | string): Observable<GlossaryTerm> {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/documentation/glossary')
      .addSegment(`/${id}`)
      .get;

    return this.httpClient.get<GlossaryTerm>(endpointURL);
  }

  public getStartingLetters(): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/documentation/glossary')
      .addSegment('/startingLetters')
      .get;

    return this.httpClient.get<[{ startingLetter: string }]>(endpoint).pipe(
      map(response => response.map(v => v.startingLetter))
    );
  }
}
